/**
 * Bulk "Self-Heal Now" for the Print Files tab: which rows can take it, which
 * selected ids it runs on, and the bounded runner that calls the existing
 * per-issue `printAgentAdmin.selfHealCustomNow` over a selection.
 *
 * The runner keeps at most 3 heals in flight: the server runs every
 * custom-source heal through one shared cap of 4 (`runBoundedCustomSourceHeal`),
 * so a fourth slot stays free for the sweep and for other operators.
 *
 * The cap counts server HEALS, not requests: a `healing` answer comes back at
 * the 20 s deadline while that heal still holds a server slot. So after a
 * `healing` answer the runner keeps its own slot busy for a further
 * `BULK_SELF_HEAL_HEALING_HOLD_MS` before starting the next heal; otherwise
 * the run would overrun the server cap and starve other operators.
 *
 * Server answers:
 *   - `already_running`: a heal for this issue is already going, so it reads
 *     as `healing` (same as the server's own re-check mapping).
 *   - `concurrency_cap`: "busy right now", not "broken". Retried with growing
 *     waits (3 s, 6 s, 12 s); still busy after that, the issue lands in its
 *     own `busy` bucket ("not started, try again"), never in "still failing".
 */
import { runWithConcurrency } from '@/lib/run-with-concurrency';
import type { SelfHealNowResult } from './hooks/useSelfHealCustomNow';
import type { ProductionIssue } from './types';

/** Heals in flight at once. Below the server's shared cap of 4. */
export const BULK_SELF_HEAL_CONCURRENCY = 3;
/** First wait before retrying a busy answer; doubles on each further try. */
export const BULK_SELF_HEAL_RETRY_DELAY_MS = 3000;
/** Retries of a `concurrency_cap` answer before it counts as `busy`. */
export const BULK_SELF_HEAL_BUSY_RETRIES = 3;
/**
 * How long a runner slot stays taken after a `healing` answer: the heal goes
 * on holding a server slot past the 20 s answer, so we wait about one more
 * deadline before starting another.
 */
export const BULK_SELF_HEAL_HEALING_HOLD_MS = 20_000;
/** Stop is checked this often during the healing hold. */
const HOLD_TICK_MS = 1000;

/**
 * The card's own rule for showing "Self-Heal Now" (an open missing file with a
 * stored artwork URL), minus a platen variant size, which never gets a
 * checkbox. Mirrors the server's `listSelfHealIssueIds`.
 */
export function isSelfHealEligible(issue: ProductionIssue): boolean {
  return (
    issue.status === 'OPEN' &&
    issue.metadata?.reason === 'file_missing' &&
    issue.bridgeEligible === true &&
    issue.platenVariantFileNeeded !== true
  );
}

/**
 * The selected ids a bulk self-heal runs on: those known to be eligible,
 * either from a row on this page or from the server's "Select all N" ids
 * (every id there is eligible by construction). Selection order is kept.
 */
export function selfHealIdsForSelection(
  selected: ReadonlySet<string>,
  eligibleOnPage: ReadonlySet<string>,
  allMatchingIds: readonly string[] | null,
): string[] {
  const fromServer = allMatchingIds ? new Set(allMatchingIds) : null;
  return Array.from(selected).filter((id) => eligibleOnPage.has(id) || fromServer?.has(id) === true);
}

export type BulkSelfHealOutcome = 'healed' | 'requeued' | 'healing' | 'failed' | 'busy' | 'stopped';

export interface BulkSelfHealTally {
  healed: number;
  requeued: number;
  healing: number;
  failed: number;
  /** Never started: the server stayed at its heal cap through every retry. */
  busy: number;
  /** Never started because the operator pressed Stop. */
  stopped: number;
}

export interface RunBulkSelfHealOptions {
  /** Checked before each heal (and before a retry): true = start no more. */
  shouldStop: () => boolean;
  /** Called as each started heal settles, for done / total progress. */
  onSettled?: (outcome: BulkSelfHealOutcome) => void;
  concurrency?: number;
  retryDelayMs?: number;
  busyRetries?: number;
  healingHoldMs?: number;
  /** Injectable for tests. */
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function isAtCap(result: SelfHealNowResult): boolean {
  return result.status === 'failed' && result.reason === 'concurrency_cap';
}

function toOutcome(result: SelfHealNowResult): BulkSelfHealOutcome {
  if (result.status === 'failed') {
    if (result.reason === 'already_running') return 'healing';
    if (result.reason === 'concurrency_cap') return 'busy';
    return 'failed';
  }
  return result.status;
}

/**
 * Run `heal` over `ids` with bounded concurrency. Never throws: a thrown heal
 * (auth, validation, network) counts as failed and the batch carries on.
 */
export async function runBulkSelfHeal(
  ids: readonly string[],
  heal: (issueId: string) => Promise<SelfHealNowResult>,
  options: RunBulkSelfHealOptions,
): Promise<BulkSelfHealTally> {
  const {
    shouldStop,
    onSettled,
    concurrency = BULK_SELF_HEAL_CONCURRENCY,
    retryDelayMs = BULK_SELF_HEAL_RETRY_DELAY_MS,
    busyRetries = BULK_SELF_HEAL_BUSY_RETRIES,
    healingHoldMs = BULK_SELF_HEAL_HEALING_HOLD_MS,
    sleep = defaultSleep,
  } = options;

  // Keep this runner slot taken while the server heal it started may still
  // hold a server slot. Ends early on Stop: no more heals start then anyway.
  const holdSlot = async () => {
    for (let waited = 0; waited < healingHoldMs && !shouldStop(); waited += HOLD_TICK_MS) {
      await sleep(Math.min(HOLD_TICK_MS, healingHoldMs - waited));
    }
  };

  const healOne = async (issueId: string): Promise<BulkSelfHealOutcome> => {
    if (shouldStop()) return 'stopped';
    let outcome: BulkSelfHealOutcome;
    try {
      let result = await heal(issueId);
      for (let attempt = 0; attempt < busyRetries && isAtCap(result); attempt += 1) {
        await sleep(retryDelayMs * 2 ** attempt);
        // Stop pressed during the wait: leave this one for later, untouched.
        if (shouldStop()) return 'stopped';
        result = await heal(issueId);
      }
      outcome = toOutcome(result);
    } catch {
      outcome = 'failed';
    }
    onSettled?.(outcome);
    if (outcome === 'healing') await holdSlot();
    return outcome;
  };

  const outcomes = await runWithConcurrency(
    ids.map((id) => () => healOne(id)),
    concurrency,
  );

  const tally: BulkSelfHealTally = { healed: 0, requeued: 0, healing: 0, failed: 0, busy: 0, stopped: 0 };
  for (const outcome of outcomes) tally[outcome] += 1;
  return tally;
}
